import React, { useState, useEffect } from 'react';
import './App.css';

// ============================================
// CONFIGURATION
// ============================================

// URL de l'API backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/notes';;

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function App() {
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================

  useEffect(() => {
    loadNotes();
  }, []);

  // ============================================
  // FONCTIONS API
  // ============================================

  // GET - Charger toutes les notes depuis l'API
  async function loadNotes() {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      setNotes(data.data || []);
      
    } catch (error) {
      console.error('Erreur chargement:', error);
      setError('Impossible de charger les notes. Le serveur est-il démarré ?');
    } finally {
      setLoading(false);
    }
  }

  // POST/PUT - Sauvegarder une note (créer ou modifier)
  async function saveNote() {
    if (!currentNote.title.trim()) {
      alert('Le titre est obligatoire');
      return;
    }

    try {
      const isUpdate = currentNote.id;
      const url = isUpdate ? `${API_URL}/${currentNote.id}` : API_URL;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: currentNote.title,
          content: currentNote.content
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      // Recharger les notes après sauvegarde
      await loadNotes();
      
      // Réinitialiser le formulaire
      setCurrentNote({ title: '', content: '' });
      
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  }

  // DELETE - Supprimer une note
  async function deleteNote(noteId) {
    if (!window.confirm('Supprimer cette note ?')) return;

    try {
      const response = await fetch(`${API_URL}/${noteId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      // Recharger les notes après suppression
      await loadNotes();
      
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  }

  // ============================================
  // FONCTIONS UI
  // ============================================

  function editNote(note) {
    setCurrentNote({
      id: note.id,
      title: note.title,
      content: note.content || ''
    });
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour : '2-digit',
      minute : "2-digit"
    });
  }

  // ============================================
  // AFFICHAGE - ÉTAT DE CHARGEMENT
  // ============================================

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement des notes...</p>
      </div>
    );
  }

  // ============================================
  // AFFICHAGE - ERREUR DE CONNEXION
  // ============================================

  if (error) {
    return (
      <div className="error-screen">
        <div className="error-icon">⚠️</div>
        <h2>Erreur de Connexion</h2>
        <p>{error}</p>
        <div className="error-help">
          <h3>Vérifiez que :</h3>
          <ul>
            <li>✅ Le serveur backend est démarré (<code>node server.js</code>)</li>
            <li>✅ Le serveur tourne sur le port 5000</li>
            <li>✅ L'URL de l'API est correcte: {API_URL}</li>
          </ul>
        </div>
        <button onClick={loadNotes} className="btn-retry">
          🔄 Réessayer
        </button>
      </div>
    );
  }

  // ============================================
  // AFFICHAGE - APPLICATION PRINCIPALE
  // ============================================

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <h1>📝 Mes Notes (Backend API)</h1>
          <div className="connection-status">
            <span className="status-dot"></span>
            Connecté à l'API
          </div>
        </div>
        <div className="header-right">
          <button onClick={loadNotes} className="btn-refresh">
            🔄 Actualiser
          </button>
        </div>
      </header>

      <div className="container">
        <div className="sidebar">
          <div className="form-card">
            <h2>{currentNote.id ? '✏️ Modifier' : '✨ Nouvelle note'}</h2>
            
            <input
              type="text"
              placeholder="Titre de la note"
              value={currentNote.title}
              onChange={(e) => setCurrentNote({...currentNote, title: e.target.value})}
              className="input-title"
            />

            <textarea
              placeholder="Contenu de la note..."
              value={currentNote.content}
              onChange={(e) => setCurrentNote({...currentNote, content: e.target.value})}
              className="textarea-content"
            />

            <button onClick={saveNote} className="btn-save">
              💾 {currentNote.id ? 'Mettre à jour' : 'Créer la note'}
            </button>

            {currentNote.id && (
              <button 
                onClick={() => setCurrentNote({ title: '', content: '' })} 
                className="btn-cancel"
              >
                Annuler
              </button>
            )}
          </div>
                
        </div>

        <div className="main-content">
          {notes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <p>Aucune note dans la base de données</p>
              <p className="empty-hint">Créez votre première note ! 👈</p>
            </div>
          ) : (
            <div className="notes-grid">
              {notes.map((note) => (
                <div key={note.id} className="note-card" onClick={() => editNote(note)}>
                  <div className="note-header">
                    <h3>{note.title}</h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNote(note.id);
                      }}
                      className="btn-delete"
                    >
                      🗑️
                    </button>
                  </div>
                  <p className="note-content">{note.content || 'Pas de contenu'}</p>
                  <div className="note-footer">
                    <span className="note-date">
                      {formatDate(note.updated_at || note.created_at)}
                    </span>
                    <span className="note-hint">Cliquez pour modifier</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="stats">
        <h3>📊 Statistiques</h3>
        <div className="stats-content">
          <div className="stat-item">
            <span className="stat-label">Total de notes :</span>
            <strong className="stat-value">{notes.length}</strong>
          </div>
          <div className="stat-item">
            <span className="stat-label">Stockage :</span>
            <strong className="stat-value">Base de données SQLite</strong>
          </div>
          <div className="stat-item">
            <span className="stat-label">API :</span>
            <strong className="stat-value">{API_URL}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPRENDRE CE FICHIER
// ============================================

/*
DIFFÉRENCES AVEC LA VERSION LOCALSTORAGE :

Ancien (localStorage) :
localStorage.setItem('notes', JSON.stringify(notes));

Nouveau (API) :
const response = await fetch('http://localhost:5000/api/notes', {
  method: 'POST',
  body: JSON.stringify(note)
});

FLUX DE DONNÉES :

1. L'utilisateur clique sur "Créer la note"
2. saveNote() est appelée
3. fetch() envoie une requête HTTP POST au backend
4. Le backend reçoit la requête
5. Le backend sauvegarde dans SQLite
6. Le backend renvoie une réponse JSON
7. Le frontend recharge les notes
8. L'UI se met à jour

ASYNC/AWAIT :

async function loadNotes() {
  const response = await fetch(API_URL);  // Attendre la réponse
  const data = await response.json();     // Attendre le parsing JSON
  setNotes(data.data);                    // Mettre à jour l'état
}

GESTION D'ERREURS :

try {
  // Code qui peut échouer
  const response = await fetch(...);
  if (!response.ok) {
    throw new Error('Erreur HTTP');
  }
} catch (error) {
  // Afficher l'erreur à l'utilisateur
  setError(error.message);
}

CODES HTTP À CONNAÎTRE :

200 OK        → Tout va bien
201 Created   → Ressource créée
400 Bad Request → Données invalides
404 Not Found → Ressource non trouvée
500 Server Error → Erreur serveur

FETCH API :

// GET (récupérer)
fetch('http://localhost:5000/api/notes')

// POST (créer)
fetch('http://localhost:5000/api/notes', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ title: 'Test', content: 'Hello' })
})

// PUT (modifier)
fetch('http://localhost:5000/api/notes/1', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ title: 'Modifié' })
})

// DELETE (supprimer)
fetch('http://localhost:5000/api/notes/1', {
  method: 'DELETE'
})
*/
